import { growsPastPromptLimit } from "@/lib/ai/roles/types"
import { normalizeNewlines } from "@/lib/utils"

import type { PromptEditPayload } from "./schema"

export type PromptEditResult =
  | {
      ok: true;
      instructions: string;
      /** Its target as the instructions held it, not as quoted; a removal also records where it stood. */
      edit: PromptEditPayload;
    }
  | { ok: false; reason: "target_missing" | "target_ambiguous" }

export type PromptUndoResult = PromptEditResult | { ok: false; reason: "target_present" }

export type UnsavableInstructions = "empty" | "too_long"

export function unsavableInstructionsMessage(reason: UnsavableInstructions, limit: number): string {
  switch (reason) {
    case "empty":
      return "This change would leave the agent without instructions. Edit them yourself instead."
    case "too_long":
      return `The instructions would pass ${limit.toLocaleString("en-US")} characters. Shorten them first.`
    default: {
      const exhaustive: never = reason
      return exhaustive
    }
  }
}

type AddEdit = Extract<PromptEditPayload, { operation: "append" }>
type RemoveEdit = Extract<PromptEditPayload, { operation: "remove" }>

/**
 * A change addresses one line. A target is compared normalised — case, whitespace, quote and dash shapes, the closing
 * punctuation and a list marker aside — and applies only when it matches exactly once, so a line edited since is
 * refused. A target of several lines matches them in a row; a paragraph quoted whole still resolves as that paragraph.
 */
export function applyPromptEdit(instructions: string, edit: PromptEditPayload): PromptEditResult {
  const lines = splitLines(instructions)

  switch (edit.operation) {
    case "append":
      return { ok: true, instructions: add(lines, edit), edit }
    case "replace": {
      const found = findTarget(lines, edit.target)
      if (!found.ok) return found
      return {
        ok: true,
        instructions: replace(lines, found.range, edit.instruction),
        edit: { ...edit, target: rangeText(lines, found.range) },
      }
    }
    case "remove": {
      const found = findTarget(lines, edit.target)
      if (!found.ok) return found
      return {
        ok: true,
        instructions: remove(lines, found.range),
        edit: { operation: "remove", target: rangeText(lines, found.range), ...placement(lines, found.range) },
      }
    }
    default: {
      const exhaustive: never = edit
      return exhaustive
    }
  }
}

/**
 * Takes an applied edit back, as it was stored, from the instructions as they are now. Refused when a line it removed
 * or rewrote was put back meanwhile: the take-back would leave it there twice.
 */
export function undoPromptEdit(instructions: string, edit: PromptEditPayload): PromptUndoResult {
  const back = applyPromptEdit(instructions, invertPromptEdit(edit))
  if (!back.ok || edit.operation === "append") return back

  const restored = findTarget(splitLines(back.instructions), edit.target)
  return !restored.ok && restored.reason === "target_ambiguous" ? { ok: false, reason: "target_present" } : back
}

/** An addition or a rewrite holds while its line is there; a removal while its line stays out. */
export function editHolds(instructions: string, edit: PromptEditPayload): boolean {
  switch (edit.operation) {
    case "append":
    case "replace":
      return holdsLine(instructions, edit.instruction)
    case "remove":
      return !holdsLine(instructions, edit.target)
    default: {
      const exhaustive: never = edit
      return exhaustive
    }
  }
}

/** The line a change writes, and the line it rewrites or removes. */
export function editLines(edit: PromptEditPayload): { written: string | null; target: string | null } {
  return {
    written: edit.operation === "remove" ? null : edit.instruction,
    target: edit.operation === "append" ? null : edit.target,
  }
}

/** Whether a line of the instructions is one of the text's lines, compared as targets are. */
export function isLineOf(line: string, text: string): boolean {
  const key = lineKey(line)
  return Boolean(key) && splitLines(text).some((candidate) => lineKey(candidate) === key)
}

/** A tool's name as the agent's instructions write it. */
export const SNAKE_CASE_NAME = /\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g

/** Whether the instructions hold the text exactly once, as a change's target must. */
export function holdsLineOnce(instructions: string, text: string): boolean {
  return findTarget(splitLines(instructions), text).ok
}

/** Whether the instructions hold the text as a line, or as lines in a row, once or more. */
export function holdsLine(instructions: string, text: string): boolean {
  const found = findTarget(splitLines(instructions), text)
  return found.ok || found.reason === "target_ambiguous"
}

/**
 * Whether a line of the instructions holds at least `share` of the text's words, and two of them at least: the text
 * reworded, not removed. Words under three letters are left out, so «be», «to» and «in» make no match.
 */
export function hasLineSharing(instructions: string, text: string, share: number): boolean {
  const wanted = wordsOf(text)
  return splitLines(instructions).some((line) => {
    const words = wordsOf(line)
    const shared = [...wanted].filter((word) => words.has(word)).length
    return shared >= MIN_SHARED_WORDS && shared / wanted.size >= share
  })
}

/**
 * Whether a line of the instructions says much the same as the text: a word-set overlap of at least `share`, two words
 * at least, a tool's name one word. Symmetric, so a line on the same template for another subject is not the text.
 */
export function hasSimilarLine(instructions: string, text: string, share: number): boolean {
  const wanted = wordsOf(text, { wholeNames: true })
  return splitLines(instructions).some((line) => {
    const words = wordsOf(line, { wholeNames: true })
    const shared = [...wanted].filter((word) => words.has(word)).length
    const all = new Set([...wanted, ...words]).size
    return shared >= MIN_SHARED_WORDS && shared / all >= share
  })
}

/**
 * An article said again: much the same words as wholes, however their lines break, and the same figures — a changed
 * figure or date is a corrected fact, not the refused one (short figures fall out of the word overlap).
 */
export function isSameArticle(text: string, other: string, share: number): boolean {
  const oneLine = (value: string) => value.replace(/\s+/g, " ")
  return hasSimilarLine(oneLine(other), oneLine(text), share) && sameFigures(text, other)
}

function sameFigures(text: string, other: string): boolean {
  const figures = (value: string) => new Set(value.match(/\d+(?:[.,]\d+)*/g) ?? [])
  const [mine, theirs] = [figures(text), figures(other)]
  return mine.size === theirs.size && [...mine].every((figure) => theirs.has(figure))
}

/** Why a changed text may not be saved: it leaves the agent without instructions, or grows them past the limit. */
export function unsavableInstructions(next: string, current: string, limit: number): UnsavableInstructions | null {
  if (!next.trim()) return "empty"
  return growsPastPromptLimit(next, current, limit) ? "too_long" : null
}

/** The drafted edit with the line as the reviewer has it; a removal takes none. */
export function withReviewedInstruction(edit: PromptEditPayload, instruction: string): PromptEditPayload {
  return edit.operation === "remove" ? edit : { ...edit, instruction }
}

function splitLines(text: string): string[] {
  return normalizeNewlines(text).split("\n")
}

// Built at runtime: the compile target predates Unicode property escapes.
const NON_WORD = new RegExp("[^\\p{L}\\p{N}]+", "u")
const MIN_SHARED_WORDS = 2

/** Words of three letters or more; with `wholeNames`, a snake_case name stays one word. */
function wordsOf(text: string, options: { wholeNames?: boolean } = {}): Set<string> {
  const normalized = normalize(text)
  const names = options.wholeNames ? (normalized.match(SNAKE_CASE_NAME) ?? []) : []
  const rest = options.wholeNames ? normalized.replace(SNAKE_CASE_NAME, " ") : normalized
  return new Set([...names, ...rest.split(NON_WORD).filter((word) => word.length >= 3)])
}

function invertPromptEdit(edit: PromptEditPayload): PromptEditPayload {
  switch (edit.operation) {
    case "append":
      return { operation: "remove", target: edit.instruction }
    case "replace":
      return { operation: "replace", target: edit.instruction, instruction: edit.target }
    case "remove":
      return {
        operation: "append",
        instruction: edit.target,
        after: edit.after,
        before: edit.before,
        joined: edit.joined,
      }
    default: {
      const exhaustive: never = edit
      return exhaustive
    }
  }
}

/** Lines `start` to `end`, the end exclusive. */
interface Range {
  start: number;
  end: number;
}

type Found = { ok: true; range: Range } | { ok: false; reason: "target_missing" | "target_ambiguous" }

// `[\s\S]`, not `.`: a line may still hold a U+2028 or U+2029 break.
const LIST_ITEM = /^(\s*)((?:[-*]|\d+[.)])\s+)?([\s\S]*)$/

function findTarget(lines: string[], target: string): Found {
  const wanted = splitLines(target.trim()).map(lineKey)
  if (!wanted[0]) return { ok: false, reason: "target_missing" }

  const keys = lines.map(lineKey)
  const starts = keys.flatMap((_, start) =>
    wanted.every((key, offset) => keys[start + offset] === key) ? [start] : []
  )
  if (starts.length > 1) return { ok: false, reason: "target_ambiguous" }
  if (starts.length === 1) return { ok: true, range: { start: starts[0], end: starts[0] + wanted.length } }

  // A paragraph quoted with its lines joined or wrapped otherwise.
  const wantedParagraph = normalize(target)
  const paragraphs = paragraphRanges(lines).filter((range) => normalize(rangeText(lines, range)) === wantedParagraph)
  if (paragraphs.length === 0) return { ok: false, reason: "target_missing" }
  if (paragraphs.length > 1) return { ok: false, reason: "target_ambiguous" }
  return { ok: true, range: paragraphs[0] }
}

/** The replaced line keeps its indentation and list marker; a paragraph is replaced as written. */
function replace(lines: string[], range: Range, instruction: string): string {
  const written = splitLines(instruction.trim())
  const oneForOne = range.end - range.start === 1 && written.length === 1
  const replacement = oneForOne ? [withShape(lines[range.start], written[0])] : written
  return [...lines.slice(0, range.start), ...replacement, ...lines.slice(range.end)].join("\n")
}

/** A line that was its own paragraph takes one blank line with it; a list or a paragraph it sat in stays whole. */
function remove(lines: string[], range: Range): string {
  const ownParagraph = !attachedAbove(lines, range) && !attachedBelow(lines, range)
  let from = range.start
  let to = range.end
  if (ownParagraph && to < lines.length) to += 1
  else if (ownParagraph && from > 0) from -= 1

  const kept = [...lines.slice(0, from), ...lines.slice(to)]
  if (to >= lines.length) trimBlankEnd(kept)
  if (from === 0) while (kept.length > 0 && isBlank(kept[0])) kept.shift()
  return kept.join("\n")
}

/** Where a removed block stood, so its inverse can put it back: next to the line it was attached to, or between. */
function placement(lines: string[], range: Range): Pick<RemoveEdit, "after" | "before" | "joined"> {
  if (attachedAbove(lines, range)) {
    return {
      after: lines[range.start - 1],
      ...(attachedBelow(lines, range) ? { before: lines[range.end] } : {}),
      joined: true,
    }
  }
  if (attachedBelow(lines, range)) return { before: lines[range.end], joined: true }

  const after = lines.slice(0, range.start).reverse().find((line) => !isBlank(line))
  const before = lines.slice(range.end).find((line) => !isBlank(line))
  return { ...(after !== undefined ? { after } : {}), ...(before !== undefined ? { before } : {}), joined: false }
}

/**
 * After the `after` line, else before the `before` line, else at the end. A drafted line (no `joined`) becomes one
 * item of a list its anchor belongs to or opens, taking the neighbour's marker — below the anchor item's own
 * sub-items — or else a paragraph of its own. An inverse (`joined` set) puts a removed block back as it stood: against
 * its anchor, or as its own paragraph.
 */
function add(lines: string[], edit: AddEdit): string {
  const restoring = edit.joined !== undefined
  const instruction = normalizeNewlines(edit.instruction)
  const text = restoring ? instruction.replace(/^(?:[ \t]*\n)+/, "").replace(/(?:\n[ \t]*)+$/, "") : instruction.trim()

  const after = edit.after === undefined ? null : findTarget(lines, edit.after)
  if (after?.ok) {
    const anchor = after.range.end - 1
    if (edit.joined) return insert(lines, anchor + 1, text.split("\n"))
    const item = restoring ? null : listItemNear(lines, anchor)
    if (item) {
      const at = item.isAnchor ? belowSubItems(lines, anchor) : anchor + 1
      return insert(lines, at, [withShape(item.line, text, item.isAnchor)])
    }
    return insertParagraph(lines, paragraphEnd(lines, anchor), text)
  }

  const before = edit.before === undefined ? null : findTarget(lines, edit.before)
  if (before?.ok) {
    if (edit.joined) return insert(lines, before.range.start, text.split("\n"))
    return insertParagraph(lines, paragraphStart(lines, before.range.start), text)
  }

  return appendParagraph(lines, text)
}

function insert(lines: string[], at: number, added: string[]): string {
  return [...lines.slice(0, at), ...added, ...lines.slice(at)].join("\n")
}

/** A paragraph of its own at line `at`, one blank line on each side. */
function insertParagraph(lines: string[], at: number, text: string): string {
  const head = lines.slice(0, at)
  const tail = lines.slice(at)
  while (head.length > 0 && isBlank(head.at(-1)!)) head.pop()
  while (tail.length > 0 && isBlank(tail[0])) tail.shift()
  return [...head, ...(head.length ? [""] : []), text, ...(tail.length ? ["", ...tail] : [])].join("\n")
}

function appendParagraph(lines: string[], text: string): string {
  const current = lines.join("\n").trimEnd()
  return current ? `${current}\n\n${text}` : text
}

/** Past the lines indented deeper than the item at `anchor`: its sub-items, which a sibling must not split. */
function belowSubItems(lines: string[], anchor: number): number {
  const depth = indentOf(lines[anchor])
  let at = anchor + 1
  while (at < lines.length && !isBlank(lines[at]) && indentOf(lines[at]) > depth) at += 1
  return at
}

/** The list item a new line after `anchor` joins: the anchor itself, or the first item it introduces. */
function listItemNear(lines: string[], anchor: number): { line: string; isAnchor: boolean } | null {
  if (isListItem(lines[anchor])) return { line: lines[anchor], isAnchor: true }
  const next = lines[anchor + 1]
  return next !== undefined && isListItem(next) ? { line: next, isAnchor: false } : null
}

/**
 * `text` in the indentation and marker of `shapeOf`. A new item after a numbered one takes the next number; text that
 * brings a marker of its own keeps it.
 */
function withShape(shapeOf: string, text: string, nextItem = false): string {
  const [, indent, marker = ""] = LIST_ITEM.exec(shapeOf)!
  const own = LIST_ITEM.exec(text)!
  if (own[2]) return `${indent}${own[2]}${own[3]}`

  const numbered = /^(\d+)([.)])(\s+)$/.exec(marker)
  const shaped = numbered && nextItem ? `${Number(numbered[1]) + 1}${numbered[2]}${numbered[3]}` : marker
  return `${indent}${shaped}${text}`
}

function attachedAbove(lines: string[], range: Range): boolean {
  return range.start > 0 && !isBlank(lines[range.start - 1])
}

function attachedBelow(lines: string[], range: Range): boolean {
  return range.end < lines.length && !isBlank(lines[range.end])
}

function paragraphRanges(lines: string[]): Range[] {
  const ranges: Range[] = []
  lines.forEach((line, index) => {
    if (isBlank(line)) return
    const last = ranges.at(-1)
    if (last && last.end === index) last.end = index + 1
    else ranges.push({ start: index, end: index + 1 })
  })
  return ranges
}

function paragraphEnd(lines: string[], index: number): number {
  let end = index + 1
  while (end < lines.length && !isBlank(lines[end])) end += 1
  return end
}

function paragraphStart(lines: string[], index: number): number {
  let start = index
  while (start > 0 && !isBlank(lines[start - 1])) start -= 1
  return start
}

function rangeText(lines: string[], range: Range): string {
  return lines.slice(range.start, range.end).join("\n")
}

function trimBlankEnd(lines: string[]): void {
  while (lines.length > 0 && isBlank(lines.at(-1)!)) lines.pop()
}

function isBlank(line: string): boolean {
  return !line.trim()
}

function isListItem(line: string): boolean {
  return Boolean(LIST_ITEM.exec(line)?.[2])
}

function indentOf(line: string): number {
  return LIST_ITEM.exec(line)![1].length
}

function lineKey(line: string): string {
  return normalize(LIST_ITEM.exec(line)![3])
}

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[‘’‚‛′`]/g, "'")
    .replace(/[“”„‟″«»]/g, '"')
    .replace(/[‐‑‒–—―−]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.!?;:…]+$/, "")
}
